import {
  faCircleNotch,
  faCircleInfo,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import {
  Connection,
  isDatabaseProvider,
  NotTestableProviders,
} from "src/api/connectApi/types";
import { startsWithInternalPrefix } from "src/api/constants";
import { BetaPill } from "src/base-components/BetaPill";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { Cell, Header } from "src/base-components/Table";
import { isBetaConnection } from "src/connections/CreateConnectionGrid";
import { ConnectionActions } from "src/connections/ListActions";
import { ProviderIcon } from "src/connections/config/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { dateFromNow, formatDate } from "src/utils/datetime";

type ConnectionColumns = {
  onDelete: (id: string) => void;
  onEdit: (id: string) => void;
  onTest: (id: string) => void;
  testingConnection?: Connection;
};

export const getTestableStatus = (connection: Connection) => {
  // Any manifest-based integration currently doesn’t support probing.
  if (connection.manifest_version) {
    return "untestable";
  }
  if (connection.provider === "webhook" || connection.provider === "retool") {
    return "untestable";
  }

  if (connection.provider === "custom") {
    const hasProductionProbeURL = Boolean(
      connection.configuration.probe_url &&
        connection.configuration.probe_url.trim(),
    );

    const hasSandboxProbeURL = Boolean(
      connection.enable_non_prod_configs &&
        connection.non_prod_env_configs?.sandbox?.probe_url &&
        connection.non_prod_env_configs?.sandbox?.probe_url.trim(),
    );

    return hasProductionProbeURL || hasSandboxProbeURL
      ? "testable"
      : "untestable_lacks_probe_url";
  }

  if (connection.configuration.multi_tenant) {
    // Temporary not allowing the multi-tenant connections
    // to be tested while we figure out how exactly it should work
    return "untestable";
  } else {
    return NotTestableProviders.includes(connection.provider)
      ? "untestable"
      : "testable";
  }
};

export const getConnectionType = (connection: Connection) => {
  if (connection.provider === "custom") {
    return "Custom Connection";
  } else if (connection.provider === "webhook") {
    return "Inbound Webhook";
  } else if (isDatabaseProvider(connection.provider)) {
    return "Database";
  } else if (
    connection.manifest_version &&
    isFeatureFlagEnabled(FEATURE_FLAGS.powertools)
  ) {
    return "Manifest Integration";
  } else {
    return "Integration";
  }
};

const columnHelper = createColumnHelper<Connection>();

export const columns = ({
  onDelete,
  onEdit,
  onTest,
  testingConnection,
}: ConnectionColumns) => [
  columnHelper.accessor("id", {
    header: (info) => (
      <Header info={info}>
        <span className="pl-1">ID</span>
        <Tooltip placement="top" title="Connection ID">
          <div className="ml-1">
            <Icon
              color="text-gray-500"
              cursorType="pointer"
              icon={faCircleInfo}
              size="2xs"
            />
          </div>
        </Tooltip>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <CopyTextIcon
          dataLoc="copy-connection-id"
          feedback="inline"
          tooltip="Copy connection ID"
          value={info.getValue()}
        />
      </Cell>
    ),
    minSize: 55,
    maxSize: 55,
    size: 55,
    filterFn: "includesString",
  }),
  columnHelper.accessor("name", {
    id: "connections",
    header: (info) => <Header info={info}>Connection name</Header>,
    filterFn: "includesString",
    cell: (info) => {
      const isInternalConnection = startsWithInternalPrefix(
        info.row.original.name,
      );
      const wrapper = (
        <span className="inline-flex items-center gap-2">
          <ProviderIcon
            manifestVersion={info.row.original.manifest_version}
            mediaKey={info.row.original.configuration.media_key}
            provider={info.row.original.provider}
            size="sm"
          />
          {isInternalConnection && (
            <Pill size="sm" variant="red">
              <Pill.Text>Internal</Pill.Text>
            </Pill>
          )}
          <span className="inline-block">{info.getValue()}</span>
          {isBetaConnection(info.row.original.provider) && (
            <BetaPill placement="right" />
          )}
        </span>
      );
      return <Cell info={info}>{wrapper}</Cell>;
    },
  }),
  columnHelper.accessor("updated_at", {
    maxSize: 180,
    size: 180,
    header: (info) => <Header info={info}>Last updated</Header>,
    cell: (info) => <Cell info={info}>{dateFromNow(info.getValue())}</Cell>,
  }),
  columnHelper.accessor("created_at", {
    id: "created",
    maxSize: 212,
    size: 212,
    header: (info) => <Header info={info}>Created at</Header>,
    cell: (info) => (
      <Cell info={info}>
        {formatDate(info.getValue(), "MMM d, u 'at' h:mm aaa")}
      </Cell>
    ),
  }),
  columnHelper.accessor((row) => getConnectionType(row), {
    id: "type",
    maxSize: 180,
    size: 180,
    header: (info) => <Header info={info}>Type</Header>,
    filterFn: "includesString",
    cell: (info) => {
      const wrapper = (
        <Pill size="sm" variant="gray">
          <Pill.Text>{info.getValue()}</Pill.Text>
        </Pill>
      );
      return <Cell info={info}>{wrapper}</Cell>;
    },
  }),
  columnHelper.display({
    id: "actions",
    maxSize: 100,
    size: 100,
    header: (info) => <Header info={info} />,
    cell: (info) => {
      const isTestingConnection =
        testingConnection?.id === info.row.original.id;
      return (
        <Cell info={info}>
          {isTestingConnection ? (
            <span className="flex w-21 items-center">
              <div className="mr-1">
                <Icon icon={faCircleNotch} spin={true} />
              </div>
              Testing...
            </span>
          ) : (
            <ConnectionActions
              rowId={info.row.original.id}
              testableStatus={getTestableStatus(info.row.original)}
              onDelete={() => onDelete(info.row.original.id)}
              onEdit={() => onEdit(info.row.original.id)}
              onTest={() => onTest(info.row.original.id)}
            />
          )}
        </Cell>
      );
    },
  }),
];
